import {applyBranding} from './applyBranding'
import {fetchTenantConfig, resolveTenant} from './tenantConfig'
import {setTenantConfig} from './tenantState'

const TIMEOUT_MS = 1500

/**
 * Fetch + apply the active tenant's branding before first render. Bounded + best-effort: on timeout or
 * any failure we resolve anyway and the portal renders with the build-time defaults (bundled Dumyah
 * logo + Metronic theme), so it never blanks out waiting on the platform API.
 *
 * Rendering anyway is deliberate — blanking the portal because a branding call was slow would be
 * worse. But the fallback is NOT silent: a non-Dumyah tenant that quietly paints Dumyah's logo and
 * runs on build-time feature defaults is the failure this whole module exists to prevent, and until
 * now it left no trace anywhere. Log it loudly instead, so the wrong-tenant look is diagnosable from
 * the console rather than mistaken for a branding-config problem on the platform side.
 */
export async function bootstrapTenantBranding(): Promise<void> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const payload = await fetchTenantConfig(controller.signal)
    // Record the config for the UI (name + feature gates) BEFORE painting, so
    // synchronous reads in tenantState are populated by first render.
    setTenantConfig(payload)
    applyBranding(payload)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      `[tenant] tenant-config fetch failed for "${resolveTenant()}" — rendering with BUILD-TIME ` +
        'defaults (bundled Dumyah branding + default feature flags), which is wrong for every ' +
        'other tenant.',
      err
    )
  } finally {
    clearTimeout(timer)
  }
}
